import React, { FC, useState, useCallback, useEffect } from 'react';
import cs from 'classnames';
import { Box } from '@wix/design-system';
import { Platform } from '@wix/ambassador-wixstudio-templates-v1-template/types';
import { VIEW_MODE, ViewTemplateData } from '@common/types';
import { specificTemplateDetailsPageInTemplatesMarketplaceGenericClick } from '@wix/bi-logger-partners-data/v2';
import { useBi } from '@components/Hooks/useBi';
import WixStudioToolBar from '@components/TemplateViewer/WixStudioNewToolBar';
import { addParamToUrl } from '@utils/urlUtils';
import MadeByHeader from './MadeByHeader';
import PurchaseErrorNotification from '@components/Template/PurchaseErrorNotification';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import s from './TemplateViewer.module.scss';

type TemplateViewerProps = {
  viewTemplateData: ViewTemplateData;
  isMobileUserAgent: boolean;
  language: string;
  entry?: string;
  isListing: boolean;
};

const TemplateViewer: FC<TemplateViewerProps> = ({
  isMobileUserAgent,
  viewTemplateData,
  entry,
  language,
  isListing,
}) => {
  const { userDetails } = useHeaderContext();

  const logger = useBi();
  const viewModeKeyToBiValue = {
    [VIEW_MODE.DESKTOP]: 'view template - Desktop view',
    [VIEW_MODE.TABLET]: 'view template - Tablet view',
    [VIEW_MODE.MOBILE]: 'view template - Mobile view',
  };

  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const [purchaseError, setPurchaseError] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState(VIEW_MODE.DESKTOP);
  const { logBiPageView } = useTemplateBi({ is_mobile: isMobileView });

  useEffect(() => {
    logBiPageView();
  }, [logBiPageView]);

  const logBiClick = (clickType: string) => {
    const origin = document.referrer;
    logger.report(
      specificTemplateDetailsPageInTemplatesMarketplaceGenericClick({
        template_id: viewTemplateData.templateId,
        editor_type: 'studio',
        template_owner_account_id: viewTemplateData.ownerAccountId,
        click_type: clickType,
        ...(origin && { origin }),
        ...(entry && { entry }),
        IsVelo: viewTemplateData.hasVeloOrBlocks,
      }),
    );
  };
  const handleViewModeChangeClick = (
    viewMode: (typeof VIEW_MODE)[keyof typeof VIEW_MODE],
  ) => {
    setViewMode(viewMode);
    logBiClick(viewModeKeyToBiValue[viewMode]);
  };
  const { editorType } = viewTemplateData;
  const getClassObject = useCallback(() => {
    return {
      [s.tablet]: viewMode === VIEW_MODE.TABLET,
      [s.mobile]:
        editorType !== Platform.WIX &&
        viewMode === VIEW_MODE.MOBILE &&
        !isMobileView,
      [s.mobileOnWixEditor]:
        viewMode === VIEW_MODE.MOBILE &&
        !isMobileView &&
        editorType === Platform.WIX,
      [s.onMobileDevice]: isMobileView,
    };
  }, [editorType, isMobileView, viewMode]);

  const getPreviewUrlByEditorAndViewMode = useCallback(() => {
    const url = addParamToUrl(viewTemplateData.templateUrl, 'rc', 'FEEDBACK');
    if (viewMode === VIEW_MODE.MOBILE || isMobileView) {
      return addParamToUrl(url, 'showMobileView', 'true');
    }
    return url;
  }, [viewMode, viewTemplateData.templateUrl, isMobileView]);

  return (
    <div className={s.studioRoot} data-hook="template-viewer-page">
      <PurchaseErrorNotification
        isError={purchaseError}
        setPurchaseError={setPurchaseError}
        isMobileView={isMobileView}
        isViewer={true}
      />
      <Box
        direction="vertical"
        boxShadow="0px 2px 4px 0px #0006241F"
        zIndex={1}
      >
        <WixStudioToolBar
          hasCustomCode={viewTemplateData.hasVeloOrBlocks}
          isListing={isListing}
          logBiClick={logBiClick}
          editorType={editorType}
          isMobileView={isMobileView}
          price={viewTemplateData.price}
          templateId={viewTemplateData.templateId}
          dataHook="view-template-toolbar"
          toggleViewMode={handleViewModeChangeClick}
          viewMode={viewMode}
          language={language}
          userDetails={userDetails}
          setPurchaseError={setPurchaseError}
          privacyPolicyUrl={viewTemplateData?.privacyPolicyUrl}
        />
        <MadeByHeader
          businessName={viewTemplateData.businessName}
          textSize={isMobileView ? 'tiny' : 'small'}
          editorType={editorType}
        />
      </Box>
      <div className={s.studioRoot}>
        <div className={s.container}>
          <iframe
            data-hook="template-viewer-iframe"
            width="100%"
            height="100%"
            title={viewTemplateData.title}
            src={getPreviewUrlByEditorAndViewMode()}
            className={cs(s.iframe, getClassObject())}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateViewer;
